<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import CardTaskStatusBadge from './CardTaskStatusBadge.vue';
import {
  TASK_MOVES,
  TASK_STATUS_STYLES,
  taskStatusLabel,
} from '../cardStatus';
import { isStartable } from '../visitTask';

const props = defineProps({
  task: { type: Object, required: true },
  prominent: { type: Boolean, default: false },
  // Ends the menu with "Excluir", apart from the statuses; only on the card page.
  deletable: { type: Boolean, default: false },
});

const emit = defineEmits(['change', 'delete']);

const { t } = useI18n();
const isOpen = ref(false);

const moves = computed(() =>
  Object.fromEntries(
    Object.entries(TASK_MOVES[props.task.status]).filter(
      ([status]) => status !== 'in_progress' || isStartable(props.task)
    )
  )
);

// The status the task is in, then the ones it can move to.
const menuItems = computed(() =>
  [props.task.status, ...Object.keys(moves.value)].map(status => ({
    label: taskStatusLabel(props.task.status_labels, status, t),
    value: status,
    action: 'status',
    icon: TASK_STATUS_STYLES[status].icon,
    isSelected: status === props.task.status,
  }))
);

const menuSections = computed(() => [
  { items: menuItems.value },
  {
    items: [
      {
        label: t('CARDS.TASKS.DELETE'),
        value: 'delete',
        action: 'delete',
        icon: 'i-lucide-trash-2',
      },
    ],
  },
]);

const onAction = ({ action, value }) => {
  isOpen.value = false;
  if (action === 'delete') emit('delete');
  else if (moves.value[value]) emit('change', moves.value[value]);
};
</script>

<template>
  <div v-on-clickaway="() => (isOpen = false)" class="relative flex-shrink-0">
    <button
      type="button"
      class="flex rounded-md hover:opacity-80"
      data-testid="task-status-select"
      @click="isOpen = !isOpen"
    >
      <CardTaskStatusBadge
        :status="task.status"
        :labels="task.status_labels"
        :prominent="prominent"
      >
        <span class="i-lucide-chevron-down size-3.5" />
      </CardTaskStatusBadge>
    </button>
    <DropdownMenu
      v-if="isOpen"
      :menu-items="deletable ? [] : menuItems"
      :menu-sections="deletable ? menuSections : []"
      class="z-50 w-44 mt-1 top-full ltr:right-0 rtl:left-0"
      @action="onAction"
    />
  </div>
</template>
